/**
 * Post views — a person's post seen as a card outside the feed
 * (docs/plans/POST_SAVE_ATTRIBUTION.md §2: save rate = savers ÷ other people
 * who saw the post).
 *
 * Where a post's sightings are recorded:
 *  - a card in the Discover feed: `view` (useViewTracking);
 *  - its own page opened: a content_interactions `view`
 *    (useRecordContentView), which Creator Insights, the digest and post
 *    ranking also read;
 *  - a card anywhere (Home's strips, a profile, a board's page — and the feed,
 *    which so records both): `post_view`, recorded here from UnifiedCard. One
 *    row per person and post (the table's UNIQUE(user_id, content_id,
 *    signal_type)), its source naming the page; the audience counts people.
 *
 * Its own signal type, not `view`, on purpose: a `view`'s source decides the
 * feed's seen-demotion (`source LIKE 'discover_%'`), and the upsert keeps the
 * LAST source, so a page view stored as a `view` would un-see a post the
 * person had scrolled past in the feed.
 *
 * Who counts is the server's business (postAudienceSql): a post's creator and
 * its trip's own people never do. The client only skips the viewer's own
 * posts, which would be thrown away anyway.
 */

import { useCallback, useEffect, useRef } from 'react';
import ActivitySignalsService from './activitySignalsService';

export const POST_VIEW_TYPES = new Set(['trip', 'album', 'activity', 'collection', 'board']);

// Keyed by viewer too: signing out and in as someone else doesn't reload the
// page, and the next person's sightings must still count.
const seenThisSession = new Set();
const seenKey = (userId, type, id) => `${userId}:${type}:${id}`;

/**
 * Record that the signed-in viewer saw this post. Once per post per session;
 * batched with the other low-priority signals.
 * @param {string} type trip | album | activity | collection | board
 * @param {string} id the post's id
 * @param {string} source the surface, e.g. 'trip_page', 'card_profile'
 */
export function recordPostView(type, id, source) {
  if (!POST_VIEW_TYPES.has(type) || id == null || id === '') return;
  const userId = ActivitySignalsService.getCurrentUserId();
  if (!userId) return;
  const key = seenKey(userId, type, id);
  if (seenThisSession.has(key)) return;
  seenThisSession.add(key);
  ActivitySignalsService._enqueueSignal({
    userId,
    contentId: String(id),
    contentType: type,
    signalType: 'post_view',
    signalValue: 0,
    source
  });
}

/** For tests: forget what this session has recorded. */
export function _resetPostViews() {
  seenThisSession.clear();
}

/** Is this normalized card someone else's post? A place card has no creator. */
export function isOthersPostCard(data) {
  return !!data && POST_VIEW_TYPES.has(data.type) && data.id != null
    && !!data.creator && !data.creator.isOwn && !data.isSponsored;
}

/**
 * A callback ref for a card's root: when someone else's post is at least half
 * on screen for the first time, a view is recorded, as the feed records its
 * `view`. The source names the page the card sits on (Home's strips are
 * 'card_home'). No-op for any other card. A callback, not an object ref, so it
 * observes whenever the root mounts — after a loading skeleton too.
 * @param {Object} data normalized card data (normalizeCardData)
 * @returns {(node: Element|null) => void}
 */
export function usePostCardView(data) {
  const observerRef = useRef(null);
  const post = isOthersPostCard(data);
  const type = data?.type;
  const id = data?.id;

  const stop = () => {
    if (observerRef.current) observerRef.current.disconnect();
    observerRef.current = null;
  };
  useEffect(() => stop, []);

  return useCallback((node) => {
    stop();
    if (!node || !post || typeof IntersectionObserver === 'undefined') return;
    if (seenThisSession.has(seenKey(ActivitySignalsService.getCurrentUserId(), type, id))) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some(e => e.isIntersecting)) {
        const page = window.location.pathname.split('/')[1] || 'home';
        recordPostView(type, id, `card_${page}`);
        stop();
      }
    }, { threshold: 0.5 });
    observerRef.current = observer;
    observer.observe(node);
  }, [post, type, id]);
}
